import React, { useEffect, useState } from "react";
import styles from "./MaterialTabView.module.scss";
import TNButton from "../../../../reusable-components/TNButton";
import AddMaterialDrawer from "../AddMaterialDrawer";
import {
  ButtonShape,
  ButtonSize,
  ButtonType,
} from "../../../../enums/ButtonAttributesEnums";
import { materialCategoryColumn, materialTabConsts } from "./constants";
import CustomTableWithPagination from "../../../../reusable-components/CustomTable";
import QuarryMaterialList from "../QuarryMaterialList";
import clsx from "clsx";
import QuarryDatabaseService from "../../../../services/quarryDatabase.service";
import { DatabaseSections } from "../../../../enums/DatabaseEnums";
import ConditionalRender from "../../../../reusable-components/ConditionalRender";
import { Spin } from "antd";

interface MaterialTabViewProps {
  sectionKey?: DatabaseSections;
  quarryId: string;
}

const MaterialTabView = ({ sectionKey, quarryId }: MaterialTabViewProps) => {
  const { ADD_MATERIAL_TXT, NO_MATERIALS_AVAILABLE } = materialTabConsts;
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
    undefined,
  );
  const [isAddMaterialOpen, setIsAddMaterialOpen] = useState<boolean>(false);
  const {
    FetchQuarryProfileCategoryList,
    FetchQuarryProfileSubCategoryAndMaterials,
  } = QuarryDatabaseService();

  const { data, isFetching, isPending } = FetchQuarryProfileCategoryList({
    quarryId: quarryId,
  });

  const {
    data: subCategoryList,
    mutateAsync: fetchSubcategoryList,
    isPending: subCategoryPending,
  } = FetchQuarryProfileSubCategoryAndMaterials({
    quarryId: quarryId,
  });

  const handleGetSubCategoryList = async (category?: string) => {
    await fetchSubcategoryList(category ?? selectedCategory);
  };

  const handleOpenAddMaterialDrawer = () => setIsAddMaterialOpen(true);
  const handleCloseAddMaterialDrawer = () => setIsAddMaterialOpen(false);

  const handleCategorySelect = (record, index) => {
    setSelectedCategory(record?.id);
    handleGetSubCategoryList(record?.id);
  };

  useEffect(() => {
    if (!isFetching && data?.length > 0) {
      setSelectedCategory(data?.[0]?.id);
      handleGetSubCategoryList(data?.[0]?.id);
    }
  }, [isFetching]);

  return (
    <>
      <AddMaterialDrawer
        isOpen={isAddMaterialOpen}
        handleCancel={handleCloseAddMaterialDrawer}
        quarryId={quarryId}
      />
      <ConditionalRender
        visible={data?.length > 0}
        hideFallback={isFetching || isPending}
        fallback={
          <div className={styles.emptyContainer}>{NO_MATERIALS_AVAILABLE}</div>
        }
      >
        <div className={styles.materialDetailsContainer}>
          <CustomTableWithPagination
            data={data}
            loading={isFetching || isPending}
            columns={materialCategoryColumn}
            className={styles.categoryListing}
            showHeader={false}
            pagination={false}
            bordered={false}
            rowClassName={(record) =>
              clsx({
                [styles.selectedCategoryRow]: record?.id === selectedCategory,
              })
            }
            isClickableTable={true}
            handleSingleSelect={handleCategorySelect}
          />
          <ConditionalRender
            visible={selectedCategory !== "" || !subCategoryPending}
          >
            <ConditionalRender
              visible={!subCategoryPending}
              hideFallback={selectedCategory === ""}
              fallback={
                <Spin
                  size={ButtonSize.LARGE}
                  className={styles.subCategoryLoader}
                />
              }
            >
              <QuarryMaterialList
                subCategoryList={subCategoryList}
                quarryId={quarryId}
                selectedCategory={selectedCategory}
                triggerRefetch={handleGetSubCategoryList}
              />
            </ConditionalRender>
          </ConditionalRender>
        </div>
      </ConditionalRender>
      <TNButton
        text={ADD_MATERIAL_TXT}
        type={ButtonType.PRIMARY}
        size={ButtonSize.LARGE}
        shape={ButtonShape.DEFAULT}
        className={styles.addMaterialButton}
        onClick={handleOpenAddMaterialDrawer}
      />
    </>
  );
};

export default MaterialTabView;
